import { Link } from "react-router-dom";
import { Gavel, Tag, Money, CreditCard, CaretDown } from "@phosphor-icons/react";
import { money, num } from "@/lib/api";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
export const longDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} ${DAYS[d.getDay()]}`;
};

export function HeroHeader({ day, rates, today, onRatesClick }) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div>
        <h1 className="font-display text-3xl font-normal tracking-tight text-slate-700 sm:text-4xl" data-testid="page-title">{longDate(day)}</h1>
        <button type="button" onClick={onRatesClick} className="mt-2 flex flex-wrap items-center gap-1.5 text-left" data-testid="dashboard-rates" title="Kuru elle güncellemek için tıklayın">
          <span className="bg-[#3b7dd8] px-2 py-0.5 font-num text-xs font-bold text-white transition-opacity hover:opacity-80" data-testid="rate-usd">$ {rates?.USD ? num(rates.USD) : "—"}</span>
          <span className="bg-[#d9534f] px-2 py-0.5 font-num text-xs font-bold text-white transition-opacity hover:opacity-80" data-testid="rate-eur">€ {rates?.EUR ? num(rates.EUR) : "—"}</span>
          {rates?.source === "manual" && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500" data-testid="rate-manual-badge">manuel</span>}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Link to="/faturalar?tur=sale" className="lift flex flex-col items-center justify-center bg-[#00897b] px-4 py-4 text-center text-white" data-testid="dashboard-promo">
          <span className="text-sm font-extrabold uppercase tracking-wide">Online Ödeme Linki</span>
          <span className="mt-1 text-xs font-semibold">faturaya link ekleyin, tahsilat otomatik işlensin</span>
        </Link>
        <div className="bg-white px-4 py-4" data-testid="kpi-day-sales">
          <div className="text-sm font-extrabold uppercase tracking-wide text-slate-600">Bugünkü Satış</div>
          <div className="mt-1 font-num text-lg text-slate-500" data-testid="kpi-day-sales-value">{money(today.sales)}</div>
        </div>
        <div className="bg-white px-4 py-4" data-testid="kpi-day-collections">
          <div className="text-sm font-extrabold uppercase tracking-wide text-slate-600">Bugünkü Tahsilat</div>
          <div className="mt-1 font-num text-lg text-slate-500" data-testid="kpi-day-collections-value">{money(today.collections)}</div>
        </div>
      </div>
    </div>
  );
}

export function BigCards({ d }) {
  const monthName = MONTHS[Number(d.day.slice(5, 7)) - 1];
  const cards = [
    ["bg-[#6d8fc7]", Gavel, d.month.sales, `${monthName} Cirosu`, "kpi-month-sales"],
    ["bg-[#f98b73]", Tag, d.month.purchases, `${monthName} Masrafları`, "kpi-month-purchases"],
    ["bg-[#4fc3e3]", Money, d.totals.stock_value, "Stok Değeri", "total-stock-value"],
  ];
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {cards.map(([bg, Icon, v, label, id], i) => (
        <div key={id} className={`fade-up lift flex items-center gap-6 px-8 py-9 text-white shadow-[0_4px_0_rgba(0,0,0,0.12)] ${bg}`} style={{ animationDelay: `${i * 60}ms` }} data-testid={id}>
          <Icon size={64} weight="fill" className="shrink-0 opacity-90" />
          <div className="min-w-0 flex-1 text-center">
            <div className="font-num text-3xl font-bold sm:text-4xl" data-testid={`${id}-value`}>{money(v).replace(" ₺", "")}</div>
            <div className="mt-1 text-lg font-medium opacity-90">{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Bars({ title, total, rows, color, testid }) {
  const max = Math.max(...rows.map((r) => r[1]), 1);
  return (
    <section className="fade-up bg-white shadow-sm" data-testid={testid}>
      <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <h3 className="text-base font-extrabold uppercase tracking-wide text-slate-700">{title}</h3>
        <span className="font-num text-lg font-bold text-slate-700" data-testid={`${testid}-total`}>{money(total)}</span>
      </header>
      <div className="space-y-4 px-10 py-5">
        {rows.map(([label, v, to, overdue]) => (
          <div key={label} data-testid={`${testid}-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between text-base">
              <Link to={to} className={`font-semibold ${color.text} hover:underline`}>{label}{overdue > 0 && <span className="ml-2 rounded bg-red-600 px-1.5 text-[10px] font-bold text-white" title="Vadesi geçen evrak">{overdue} vadesi geçti</span>}</Link>
              <span className={`font-num ${color.text}`}>{money(v)}</span>
            </div>
            <div className="mt-1 h-6 w-full bg-slate-100">
              <div className={`h-full ${color.bar} stripes`} style={{ width: `${Math.max(2, (v / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AssetsLiabilities({ d }) {
  const n = d.notes;
  const assets = [["Kasa", Math.max(0, d.cash.balance), "/kasa"], ["Açık Hesap", d.totals.receivable, "/cariler?tip=customer"],
    ["Çek", n.check_received, "/cek-senet?yon=received&tur=check", n.overdue_received], ["Senet", n.promissory_received, "/cek-senet?yon=received&tur=promissory"], ["Stok", d.totals.stock_value, "/stok"]];
  const debts = [["Açık Hesap", d.totals.payable, "/cariler?tip=supplier"], ["Verilen Çek / Senet", n.issued, "/cek-senet?yon=issued", n.overdue_issued],
    ["Bekleyen Alış Siparişi", d.open_purchase_orders, "/siparisler?tur=purchase"]];
  const sum = (rows) => rows.reduce((s, r) => s + r[1], 0);
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Bars title="Varlıklar" total={sum(assets)} rows={assets} color={{ text: "text-[#2a9d8f]", bar: "bg-[#2a9d8f]" }} testid="dashboard-assets" />
      <Bars title="Borçlar" total={sum(debts)} rows={debts} color={{ text: "text-[#c0392b]", bar: "bg-[#c0392b]" }} testid="dashboard-debts" />
      <section className="fade-up flex flex-col bg-[#3f6ea8] text-white shadow-sm" data-testid="dashboard-side-card">
        <header className="flex items-center justify-between px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-extrabold uppercase tracking-wide"><CreditCard weight="fill" /> Hızlı İşlemler</h3>
          <span className="bg-white/15 p-1"><CaretDown /></span>
        </header>
        <div className="flex flex-1 flex-col justify-center gap-3 bg-[#2f5a8d] px-6 py-8">
          <div className="font-display text-2xl font-bold leading-tight">İşleriniz Tek Ekranda Kolaylaşsın!</div>
          <p className="text-sm opacity-90">Satış faturası kesin, sipariş açın, tahsilat girin; tüm hareketler cari ve stok bakiyelerine anında işlenir.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {[["/faturalar/yeni?tur=sale", "Satış Faturası", "dashboard-new-sale-button"], ["/siparisler/yeni?tur=sale", "Sipariş", "dashboard-new-order-button"],
              ["/kasa", "Tahsilat Gir", "dashboard-new-collection-button"], ["/raporlar", "Raporlar", "dashboard-reports-button"]].map(([to, l, id]) => (
              <Link key={id} to={to} data-testid={id} className="rounded-full border border-white/70 px-3 py-2 text-center text-sm font-semibold transition-colors hover:bg-white hover:text-[#2f5a8d]">{l}</Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
